import { useState, useEffect } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabase';

export interface UserStats {
  totalBirdsIdentified: number;
  totalDetectionSessions: number;
  rareBirds: number;
}

export function useUserStats() {
  const { user } = useAuth();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setStats(null);
      setLoading(false);
      return;
    }

    async function fetchUserStats() {
      try {
        setLoading(true);
        setError(null);

        // Fetch user's bird library for total species count
        const { data: libraryData, error: libraryError } = await supabase
          .from('user_bird_library')
          .select('id, detection_count')
          .eq('user_id', user!.id);

        if (libraryError) {throw libraryError;}

        // Fetch user's detection sessions
        const { data: sessionsData, error: sessionsError } = await supabase
          .from('detection_sessions')
          .select('id')
          .eq('user_id', user!.id);

        if (sessionsError) {throw sessionsError;}

        // Calculate stats
        const totalBirdsIdentified = libraryData?.length || 0;
        const totalDetectionSessions = sessionsData?.length || 0;
        
        // Consider birds with only 1-2 detections as "rare finds"
        const rareBirds = libraryData?.filter(bird => bird.detection_count <= 2).length || 0;

        setStats({
          totalBirdsIdentified,
          totalDetectionSessions,
          rareBirds
        });
      } catch (err) {
        console.error('Error fetching user stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch stats');
        setStats(null);
      } finally {
        setLoading(false);
      }
    }

    fetchUserStats();
  }, [user]);

  return { stats, loading, error };
}